import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sendAnalytics } from '../../../analytics';
import { studentsFieldDetailsGet } from '../../../api-client/api-gqadmin-client';
import { CfButtonSection, CfOtpVerification, CfRenderRestFields, CfStudentIdentifier } from '../custom-form';
import { useQuery } from '../../../hooks';
import {
    getPaymentPagesFetch,
    patchCustomerDetailsMobileUpdate,
    postStudentsWrapperCreate,
} from '../../../reduxtoolkit/slices/gq-admin';
import { setStaticData } from '../../../reduxtoolkit/slices/gq-svc-pp';
import { ANALYTICS_EVENTS } from '../../../utils/constants';
import { hasDollarInString, isSuccessStatus, showErrorToast } from '../../../utils/utils';
import Regex from '../../../Regex';
import { generateInitialFormValues, generatePayload, generateValidationSchema, prefillCustomForm } from './helper';
import classes from './PaymentForm.module.scss';

const PaymentForm = (props) => {
    const {
        data: ppJsonData,
        studentId,
        customerNumber,
        handleDataUpdate,
        handleActiveStepUpdate,
        PROCESS_SLUGS,
        setStudentBasicInfoData,
        setPaymentPageFetchData,
    } = props;

    const dispatch = useDispatch();
    const params = useParams();
    const query = useQuery();

    const { form_block: formBlock, institute_config: instituteConfig } = ppJsonData || {};
    const { data: formBlockData } = formBlock || {};
    if (!formBlock || !formBlockData) return null;

    const [studentDetails, setStudentDetails] = useState(null);
    const [studentDetailsApiCount, setStudentDetailsApiCount] = useState(0);
    const [isStudentDetailsLoading, setIsStudentDetailsLoading] = useState(false);

    const [maskedNumberString, setMaskedNumberString] = useState('');
    const [maskedEmailString, setMaskedEmailString] = useState('');
    const [isStudentVerified, setIsStudentVerified] = useState(false);
    const [isOtpVerified, setIsOtpVerified] = useState(false);
    const [isFormSubmitApiLoading, setIsFormSubmitApiLoading] = useState(false);
    const [duplicateMobileNumber, setDuplicateMobileNumber] = useState(null);

    const enableMobileOtpVerification = formBlockData?.form_config?.enable_otp_verification ?? false;
    const enableEmailOtpVerification = formBlockData?.form_config?.enable_email_otp_verification ?? false;
    const disableSidField = formBlockData?.form_config?.disable_sid_field ?? false;

    // Primary identifier config for new flow
    const primaryIdentifier = formBlockData?.form_config?.primary_identifier; // "customer_mobile" | "customer_email"
    const isPrimaryIdentifierFlow = Boolean(primaryIdentifier);
    const isPrimaryMobile = primaryIdentifier === 'customer_mobile';
    const isPrimaryEmail = primaryIdentifier === 'customer_email';

    // Auto-OTP states for primary identifier
    const [isPrimaryOtpSent, setIsPrimaryOtpSent] = useState(false);
    const [isPrimaryOtpVerified, setIsPrimaryOtpVerified] = useState(false);
    const [maskedPrimaryString, setMaskedPrimaryString] = useState('');

    const initialValues = useMemo(
        () => generateInitialFormValues(formBlockData?.fields, studentId, customerNumber),
        [formBlockData],
    );

    // Helper functions for primary identifier flow
    const createMaskedPrimaryIdentifier = (value) => {
        if (!value) return '';

        if (isPrimaryMobile && value?.length === 10) {
            return `Enter number ending with XXXXX${value.substring(6)}`;
        }

        if (isPrimaryEmail && value.includes('@')) {
            const [local, domain] = value.split('@');
            if (local.length > 3) {
                const visiblePart = local.substring(0, 3);
                const maskedPart = 'X'.repeat(local.length - 3);
                return `Enter email starting with ${visiblePart}${maskedPart}@${domain}`;
            }
            return `Enter email starting with ${local}@${domain}`;
        }

        return '';
    };

    const shouldShowPrimaryOtp = () => {
        if (!isPrimaryIdentifierFlow) return false;

        const primaryOtpEnabled =
            (isPrimaryMobile && enableMobileOtpVerification) || (isPrimaryEmail && enableEmailOtpVerification);

        return primaryOtpEnabled && !isPrimaryOtpVerified;
    };

    const shouldShowCustomerOtpWithPrimary = () => {
        if (!isPrimaryIdentifierFlow) return false;

        // If primary OTP verification exists and is enabled,
        // skip cross OTP entirely
        const primaryOtpEnabled =
            (isPrimaryMobile && enableMobileOtpVerification) || (isPrimaryEmail && enableEmailOtpVerification);

        if (primaryOtpEnabled) return false; // ← skip cross OTP

        const primaryOtpComplete = shouldShowPrimaryOtp() ? isPrimaryOtpVerified : true;
        if (!primaryOtpComplete) return false;

        if (isPrimaryMobile && enableEmailOtpVerification) {
            return !isOtpVerified;
        }
        if (isPrimaryEmail && enableMobileOtpVerification) {
            return !isOtpVerified;
        }

        return false;
    };
    const shouldShowRestFields = () => {
        if (studentDetailsApiCount === 0) return false;

        // Primary identifier flow
        if (isPrimaryIdentifierFlow) {
            const primaryOtpComplete = shouldShowPrimaryOtp() ? isPrimaryOtpVerified : true;
            const customerOtpComplete = shouldShowCustomerOtpWithPrimary() ? isOtpVerified : true;
            return primaryOtpComplete && customerOtpComplete;
        }

        return true;
    };

    const shouldShowButton = () => {
        return shouldShowRestFields();
    };

    const handleCustomerMobilePatch = async (values) => {
        try {
            const payload = {
                ...((studentDetails?.customer_mobile || duplicateMobileNumber) && {
                    existing_mobile: studentDetails?.customer_mobile || duplicateMobileNumber,
                }),
                ...(values?.customer_mobile && { new_mobile: values?.customer_mobile }),
                ...(formik?.values?.stud_bd_student_uuid && {
                    student_uuid: formik?.values?.stud_bd_student_uuid?.trim(),
                }),
                ...(studentDetails?.stud_bd?.code && { sbd_code: studentDetails?.stud_bd?.code }),
                ...(instituteConfig?.group_code && { group_code: instituteConfig?.group_code }),
                ...(instituteConfig?.institute_code && { institute_code: instituteConfig?.institute_code }),
                ...(instituteConfig?.location_code && { location_code: instituteConfig?.location_code }),
                ...(instituteConfig?.education_type_code && {
                    education_type_code: instituteConfig?.education_type_code,
                }),
                ...(instituteConfig?.ay_code && { ay_code: instituteConfig?.ay_code }),
                ...(instituteConfig?.gilec_code && { gilec_code: instituteConfig?.gilec_code }),
            };
            const patchCustomerDetailsMobileUpdateRes = await dispatch(
                patchCustomerDetailsMobileUpdate(payload),
            ).unwrap();
            if (patchCustomerDetailsMobileUpdateRes?.status_code === 200) {
                return true;
            }
            return false;
        } catch (error) {
            console.error('handleCustomerMobilePatch error', error);
            return false;
        }
    };

    // Wrapper API Call
    const postWrapperApiData = async (values) => {
        try {
            const formFieldsPayload = await generatePayload(values, formBlockData);
            let feeGroupList = [];
            const sdkBlocks = ppJsonData?.sdk_block ?? [];
            sdkBlocks?.forEach(({ financing_mode, fee_group }) => {
                if (!fee_group?.code) return;
                const isCheckbox = ppJsonData?.is_checkbox_enabled;
                if (!isCheckbox || financing_mode !== 'DIRECT') {
                    feeGroupList.push(fee_group.code);
                }
            });
            const payload = {
                ...formFieldsPayload,
                student_uuid: values?.stud_bd_student_uuid?.trim() || studentId,
                customer_mobile:
                    duplicateMobileNumber ||
                    values?.customer_mobile ||
                    studentDetails?.customer_mobile ||
                    customerNumber,
                ...(values?.customer_email && { customer_email: values?.customer_email }),
                ...(instituteConfig?.group_code && { group_code: instituteConfig?.group_code }),
                ...(instituteConfig?.institute_code && { institute_code: instituteConfig?.institute_code }),
                ...(instituteConfig?.location_code && { location_code: instituteConfig?.location_code }),
                ...(instituteConfig?.education_type_code && {
                    education_type_code: instituteConfig?.education_type_code,
                }),
                ...(instituteConfig?.ay_code && { ay_code: instituteConfig?.ay_code }),
                ...(instituteConfig?.gilec_code && { gilec_code: instituteConfig?.gilec_code }),
                fee_group_codes: feeGroupList,
                payment_page_slug: params?.slug,
                ...(query.get('uid') && {
                    pp_redirect_uid: query.get('uid'),
                }),
            };
            const postStudentsWrapperCreateRes = await dispatch(postStudentsWrapperCreate(payload)).unwrap();
            if (postStudentsWrapperCreateRes?.status_code === 200 && postStudentsWrapperCreateRes?.data) {
                return postStudentsWrapperCreateRes?.data;
            }
            return false;
        } catch (error) {
            console.error('postWrapperApiData error', error);
            if (error?.status_code !== 401 || error?.status_code !== 403) {
                showErrorToast(error?.message || 'Oops! Something went wrong');
            }
            return false;
        }
    };

    const handleNavigateStepsOnSuccess = async (wrapperApiResponse) => {
        try {
            if (ppJsonData?.dynamic_link) {
                handleActiveStepUpdate(PROCESS_SLUGS?.DYNAMIC);
            } else if (ppJsonData?.is_checkbox_enabled) {
                handleActiveStepUpdate(PROCESS_SLUGS?.CHECKBOX);
            } else {
                const payload = {
                    payment_page_slug: params?.slug,
                    sbd_code: wrapperApiResponse?.sbd_code,
                    ...(query.get('sc') && { session_code: query.get('sc') }),
                    ...(query.get('ppsc') && { session_code: query.get('ppsc') }),
                    ...(query.get('uid') && { pp_redirect_uid: query.get('uid') }),
                };
                const getPaymentPagesFetchRes = await dispatch(getPaymentPagesFetch(payload)).unwrap();
                setPaymentPageFetchData(getPaymentPagesFetchRes?.data);
                if (wrapperApiResponse?.sbd_code) {
                    handleActiveStepUpdate(PROCESS_SLUGS?.PAYMENT_CARDS);
                }
            }
        } catch (error) {
            console.error('Error in handleNavigateStepsOnSuccess:', error);
        }
    };

    const handleCustomFormSubmit = async (values) => {
        try {
            if (values?.customer_mobile) {
                handleDataUpdate({ customer_mobile: values?.customer_mobile });
            }
            if (values?.stud_bd_student_uuid) {
                handleDataUpdate({
                    student_uuid: values?.stud_bd_student_uuid?.trim(),
                });
            }

            if (!enableMobileOtpVerification) {
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_CUSTOM_FORM_SKIP_MOBILE_VERIFICATION,
                        trackingData: {
                            form_name: formBlock?.name,
                        },
                    },
                });
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_CUSTOM_FORM_SKIP_OTP,
                        trackingData: {
                            form_name: formBlock?.name,
                        },
                    },
                });
            }
            sendAnalytics({
                vendor: ['mixpanel', 'ga'],
                eventData: {
                    event: ANALYTICS_EVENTS.PP_CUSTOM_FORM_SUBMITTED,
                    trackingData: {
                        form_name: formBlock?.name,
                    },
                },
            });

            const isWrapperApiSuccess = await postWrapperApiData(values);
            const custMobile = values?.customer_mobile || customerNumber;
            if (isWrapperApiSuccess && !hasDollarInString(custMobile)) {
                dispatch(
                    setStaticData({
                        customer_code: isWrapperApiSuccess?.customer_code,
                    }),
                );
            }

            if (isWrapperApiSuccess) {
                handleDataUpdate({
                    sbd_code: isWrapperApiSuccess?.sbd_code,
                });
                const isMobileNumUpdatedRequired =
                    duplicateMobileNumber &&
                    values?.customer_mobile &&
                    duplicateMobileNumber !== values?.customer_mobile;
                if (isMobileNumUpdatedRequired) {
                    let isMobilePatchSuccess = false;
                    isMobilePatchSuccess = await handleCustomerMobilePatch(values);
                    if (isMobilePatchSuccess) {
                        await handleNavigateStepsOnSuccess(isWrapperApiSuccess);
                    } else {
                        toast.error(
                            'We are unable to procees your request at the moment. Please contact support@grayquest.com',
                        );
                    }
                } else {
                    await handleNavigateStepsOnSuccess(isWrapperApiSuccess);
                }
            } else {
                toast.error(
                    'We are unable to procees your request at the moment. Please contact support@grayquest.com',
                );
            }
        } catch (error) {
            console.error('Error in handleCustomFormSubmit:', error);
        }
    };

    const onSubmit = async (values) => {
        try {
            if (studentDetailsApiCount === 0) return;
            setIsFormSubmitApiLoading(true);

            // Handle primary identifier flow - set customer field values from stud_bd_student_uuid
            if (isPrimaryIdentifierFlow && values?.stud_bd_student_uuid) {
                const primaryValue = values.stud_bd_student_uuid.trim();

                if (primaryIdentifier === 'customer_mobile') {
                    values.customer_mobile = primaryValue;
                } else if (primaryIdentifier === 'customer_email') {
                    values.customer_email = primaryValue;
                }
            }

            const isMobileNumUpdatedRequired =
                studentDetails &&
                values?.customer_mobile &&
                hasDollarInString(studentDetails?.customer_mobile) &&
                studentDetails?.customer_mobile !== values?.customer_mobile;
            if (isMobileNumUpdatedRequired) {
                let isMobilePatchSuccess = false;
                isMobilePatchSuccess = await handleCustomerMobilePatch(values);
                if (isMobilePatchSuccess) {
                    await handleCustomFormSubmit(values);
                } else {
                    toast.error(
                        'We are unable to procees your request at the moment. Please contact support@grayquest.com',
                    );
                }
            } else {
                await handleCustomFormSubmit(values);
            }
        } catch (error) {
            console.error('Error in CustomForm OnSubmit', error);
        } finally {
            setIsFormSubmitApiLoading(false);
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema: useMemo(
            () =>
                generateValidationSchema(formBlockData?.fields, {
                    primaryIdentifier,
                    isPrimaryIdentifierFlow,
                }),
            [formBlockData, primaryIdentifier, isPrimaryIdentifierFlow],
        ),
        enableReinitialize: true,
        onSubmit,
    });

    const updateCustomerMobileField = (studData) => {
        // Handle primary identifier flow first
        if (isPrimaryIdentifierFlow && isStudentVerified) {
            const primaryValue = formik?.values?.stud_bd_student_uuid?.trim();

            if (isPrimaryMobile && primaryValue?.length === 10) {
                // Set the mobile field value from primary identifier
                handleDataUpdate({ customer_mobile: primaryValue });
                formik.setFieldValue('customer_mobile', primaryValue);

                // Create masked string for OTP if enabled
                if (enableMobileOtpVerification && shouldShowPrimaryOtp()) {
                    const maskedString = createMaskedPrimaryIdentifier(primaryValue);
                    setMaskedPrimaryString(maskedString);
                }
            }

            if (isPrimaryEmail && primaryValue?.includes('@')) {
                // Set the email field value from primary identifier
                formik.setFieldValue('customer_email', primaryValue);

                // Create masked string for OTP if enabled
                if (enableEmailOtpVerification && shouldShowPrimaryOtp()) {
                    const maskedString = createMaskedPrimaryIdentifier(primaryValue);
                    setMaskedPrimaryString(maskedString);
                }
            }
        }

        // Handle existing mobile field logic (unchanged)
        if (studData?.customer_mobile?.length === 10 && !hasDollarInString(studData?.customer_mobile)) {
            handleDataUpdate({
                customer_mobile: studData?.customer_mobile,
            });
            if (formBlockData?.form_config?.enable_otp_verification) {
                let maskedNumberString = 'Enter number ending with XXXXX X';
                maskedNumberString += studData?.customer_mobile
                    ?.toString()
                    ?.substring(studData.customer_mobile.length - 4);
                setMaskedNumberString(maskedNumberString);
            } else {
                formik.setFieldValue('customer_mobile', studData?.customer_mobile || '');
            }
        } else if (hasDollarInString(studData?.customer_mobile)) {
            handleDataUpdate({
                customer_mobile: studData?.customer_mobile,
            });
        }

        // Handle existing email field logic (unchanged)
        if (formBlockData?.form_config?.enable_email_otp_verification && studData?.cust_bd?.email) {
            let email = studData?.cust_bd?.email?.toString()?.trim();
            let maskedEmailString = 'Enter email starting with ';

            if (email && email.includes('@')) {
                const [local, domain] = email.split('@');

                // Check for valid local and domain parts
                if (local.length > 0 && domain.length > 0) {
                    let visiblePart = local;
                    let maskedPart = '';

                    if (local.length > 3) {
                        visiblePart = local.substring(0, 3);
                        maskedPart = 'X'.repeat(local.length - 3);
                    }

                    maskedEmailString += `${visiblePart}${maskedPart}@${domain}`;
                } else {
                    // Invalid email format (like "@domain.com" or "local@")
                    maskedEmailString = 'Invalid email format';
                }
            } else {
                maskedEmailString = 'Email not available or invalid';
            }

            setMaskedEmailString(maskedEmailString);
        }
    };

    const fetchStudentDetails = async (payload) => {
        try {
            setIsStudentDetailsLoading(true);
            const studentsFieldDetailsGetRes = await studentsFieldDetailsGet(payload);
            if (isSuccessStatus(studentsFieldDetailsGetRes?.data) && studentsFieldDetailsGetRes?.data?.data) {
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_STUD_WRAPPER_API.SUCCESS,
                    },
                });
                const studData = studentsFieldDetailsGetRes?.data?.data;
                setStudentDetails(studData);
                setStudentBasicInfoData(studData?.stud_bd || null);
                prefillCustomForm(studData, formik, formBlockData);
                updateCustomerMobileField(studData);
            }
            setIsStudentVerified(true);
        } catch (error) {
            console.error('fetchStudentDetails error', error);
            formik?.setFieldValue('customer_mobile', '');
            if (error?.response?.data?.status_code && error?.response?.data?.message) {
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_STUD_WRAPPER_API.FAILED,
                        trackingData: {
                            err_message: error?.response?.data?.message,
                            err_code: error?.response?.data?.status_code,
                        },
                    },
                });
            }
            setIsStudentVerified(false);
        } finally {
            setIsStudentDetailsLoading(false);
            setStudentDetailsApiCount((prevState) => prevState + 1);
        }
    };

    const handleVerifyStudentClick = async () => {
        if (!formik?.values?.stud_bd_student_uuid || !params?.slug) return;

        const inputValue = formik?.values?.stud_bd_student_uuid?.trim();

        // Validation for primary identifier flow
        if (isPrimaryIdentifierFlow) {
            if (primaryIdentifier === 'customer_mobile') {
                // Validate mobile number (should be exactly 10 digits and start with 6-9)
                const mobilePattern = /^[6-9][0-9]{9}$/;
                if (inputValue.length !== 10 || !mobilePattern.test(inputValue)) {
                    formik.setFieldTouched('stud_bd_student_uuid', true);
                    formik.setFieldError(
                        'stud_bd_student_uuid',
                        'Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9',
                    );
                    return;
                }
            } else if (primaryIdentifier === 'customer_email') {
                // Validate email format
                const emailPattern = new RegExp(Regex.email);
                if (!emailPattern.test(inputValue)) {
                    formik.setFieldTouched('stud_bd_student_uuid', true);
                    formik.setFieldError('stud_bd_student_uuid', 'Please enter a valid email address');
                    return;
                }
            }
        }

        await fetchStudentDetails({
            student_uuid: inputValue,
            payment_page_slug: params?.slug,
        });
    };

    const handleStudentIdEdit = () => {
        formik.resetForm();
        setStudentDetails(null);
        setStudentDetailsApiCount(0);
        setStudentBasicInfoData(null);
        setMaskedNumberString('');
        setMaskedEmailString('');
        setIsStudentVerified(false);
        setIsOtpVerified(false);
        setDuplicateMobileNumber(null);

        // Reset primary identifier states
        setIsPrimaryOtpSent(false);
        setIsPrimaryOtpVerified(false);
        setMaskedPrimaryString('');
    };

    const handleAddPageAnalytics = () => {
        sendAnalytics({
            vendor: ['mixpanel', 'ga'],
            eventData: {
                event: ANALYTICS_EVENTS.PP_CUSTOM_FORM_LOADED,
                trackingData: {
                    form_name: formBlock?.name,
                },
            },
        });
    };

    const synchronizePageData = () => {
        handleAddPageAnalytics();
        // uid -- session id for fetching Options Data.
        // prefetching only in redirection flow usecase.[uid -- check]
        if (
            (query.get('uid') && formik?.values?.stud_bd_student_uuid) ||
            (disableSidField && query.get('sid') && formik?.values?.stud_bd_student_uuid)
        ) {
            const payload = {
                student_uuid: formik?.values?.stud_bd_student_uuid?.trim(),
                payment_page_slug: params?.slug,
            };
            fetchStudentDetails(payload);
        }
    };

    useEffect(() => {
        synchronizePageData();
    }, []);

    return (
        <div className={classes.PaymentFormWrap}>
            {formBlockData?.meta_data?.title && (
                <h3 className={classes.PaymentForm__Title}>{formBlockData?.meta_data?.title}</h3>
            )}
            {formBlockData?.meta_data?.subtitle && (
                <p className={classes.PaymentForm__Subtitle}>{formBlockData?.meta_data?.subtitle}</p>
            )}

            <form className={classes.PaymentForm} onSubmit={formik.handleSubmit}>
                <CfStudentIdentifier
                    formBlockData={formBlockData}
                    formik={formik}
                    studentDetails={studentDetails}
                    isStudentDetailsLoading={isStudentDetailsLoading}
                    studentDetailsApiCount={studentDetailsApiCount}
                    isStudentVerified={isStudentVerified}
                    handleVerifyStudentClick={handleVerifyStudentClick}
                    handleStudentIdEdit={handleStudentIdEdit}
                    disableSidField={disableSidField}
                    primaryIdentifier={primaryIdentifier}
                    isPrimaryIdentifierFlow={isPrimaryIdentifierFlow}
                    isPrimaryOtpVerified={isPrimaryOtpVerified}
                />
                <CfOtpVerification
                    formBlockData={formBlockData}
                    formik={formik}
                    studentDetails={studentDetails}
                    isStudentVerified={isStudentVerified}
                    studentDetailsApiCount={studentDetailsApiCount}
                    isOtpVerified={isOtpVerified}
                    setIsOtpVerified={setIsOtpVerified}
                    formBlock={formBlock}
                    maskedNumberString={maskedNumberString}
                    maskedEmailString={maskedEmailString}
                    duplicateMobileNumber={duplicateMobileNumber}
                    setDuplicateMobileNumber={setDuplicateMobileNumber}
                    customerNumber={customerNumber}
                    primaryIdentifier={primaryIdentifier}
                    isPrimaryIdentifierFlow={isPrimaryIdentifierFlow}
                    isPrimaryOtpSent={isPrimaryOtpSent}
                    setIsPrimaryOtpSent={setIsPrimaryOtpSent}
                    isPrimaryOtpVerified={isPrimaryOtpVerified}
                    setIsPrimaryOtpVerified={setIsPrimaryOtpVerified}
                    maskedPrimaryString={maskedPrimaryString}
                    shouldShowPrimaryOtp={shouldShowPrimaryOtp}
                    shouldShowCustomerOtpWithPrimary={shouldShowCustomerOtpWithPrimary}
                />
                {shouldShowRestFields() && (
                    <CfRenderRestFields
                        formBlockData={formBlockData}
                        formik={formik}
                        studentDetails={studentDetails}
                        isStudentVerified={isStudentVerified}
                        studentDetailsApiCount={studentDetailsApiCount}
                        isOtpVerified={isOtpVerified}
                        ppJsonData={ppJsonData}
                        isPrimaryIdentifierFlow={isPrimaryIdentifierFlow}
                        isPrimaryOtpVerified={isPrimaryOtpVerified}
                    />
                )}
                {shouldShowButton() && (
                    <CfButtonSection
                        formBlockData={formBlockData}
                        studentDetails={studentDetails}
                        isStudentVerified={isStudentVerified}
                        studentDetailsApiCount={studentDetailsApiCount}
                        isOtpVerified={isOtpVerified}
                        isFormSubmitApiLoading={isFormSubmitApiLoading}
                        isPrimaryIdentifierFlow={isPrimaryIdentifierFlow}
                        isPrimaryOtpVerified={isPrimaryOtpVerified}
                    />
                )}
            </form>
        </div>
    );
};

PaymentForm.propTypes = {
    data: PropTypes.object,
    studentId: PropTypes.string,
    customerNumber: PropTypes.string,
    handleDataUpdate: PropTypes.func,
    handleActiveStepUpdate: PropTypes.func,
    PROCESS_SLUGS: PropTypes.object,
    setStudentBasicInfoData: PropTypes.func,
    setPaymentPageFetchData: PropTypes.func,
};

export default PaymentForm;
